import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontalIcon, ArrowUpDownIcon, FilterIcon, XIcon } from 'lucide-react';
import { useHorizontalScroll } from '../lib/hooks/useHorizontalScroll';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedProductCard } from '../components/shared/EnhancedProductCard';
import { NowCompactProductCard } from '../components/now/NowCompactProductCard';
import { FilterSidebar } from '../components/category/FilterSidebar';
import { MobileFilterModal } from '../components/category/MobileFilterModal';
import { MobileSortModal } from '../components/category/MobileSortModal';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { Pagination } from '../components/shared/Pagination';
import { NowCategoryBrowseHeader } from '../components/now/NowCategoryBrowseHeader';
import { pharmacyCategories } from '../data/categories';
interface CategoryPLPProps {
  categoryName: string;
  categoryDescription: string;
  isNowContext?: boolean;
}
export function CategoryPLP({
  categoryName,
  categoryDescription,
  isNowContext = false
}: CategoryPLPProps) {
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const ITEMS_PER_PAGE = 20;
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    brands: [] as string[],
    priceRange: {
      min: 0,
      max: 5000
    },
    categories: [] as string[],
    needsPrescription: false,
    isExpress: false,
    isGlobal: false,
    offers: [] as string[]
  });
  const [sortBy, setSortBy] = useState('best-match');
  const [nowAvailability, setNowAvailability] = useState<'all' | 'available' | 'limited'>('all');
  const [nowCategoryId, setNowCategoryId] = useState(() => pharmacyCategories.find(category => category.id === categoryName.toLowerCase().replace(/\s+/g, '-'))?.id ?? 'medicines');
  const [nowSubcategory, setNowSubcategory] = useState('All');
  useEffect(() => {
    // Show FAB immediately on mobile
    setShowFab(true);
    const handleScroll = () => {
      // Optional: hide/show based on scroll direction or position if needed
      // For now, keeping it always visible for better UX
      setShowFab(true);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const activeFilters = useMemo(() => {
    const filters: {
      id: string;
      label: string;
      type: string;
    }[] = [];
    selectedFilters.brands.forEach(brand => {
      filters.push({
        id: `brand-${brand}`,
        label: brand,
        type: 'brand'
      });
    });
    selectedFilters.categories.forEach(cat => {
      filters.push({
        id: `cat-${cat}`,
        label: cat,
        type: 'category'
      });
    });
    selectedFilters.offers.forEach(offer => {
      filters.push({
        id: `offer-${offer}`,
        label: offer,
        type: 'offer'
      });
    });
    if (selectedFilters.needsPrescription) {
      filters.push({
        id: 'rx',
        label: 'Prescription Required',
        type: 'rx'
      });
    }
    if (selectedFilters.priceRange.min > 0 || selectedFilters.priceRange.max < 5000) {
      filters.push({
        id: 'price',
        label: `${selectedFilters.priceRange.min} - ${selectedFilters.priceRange.max} SAR`,
        type: 'price'
      });
    }
    return filters;
  }, [selectedFilters]);
  const removeFilter = (filter: {
    id: string;
    label: string;
    type: string;
  }) => {
    if (filter.type === 'brand') {
      setSelectedFilters(prev => ({
        ...prev,
        brands: prev.brands.filter(b => b !== filter.label)
      }));
    } else if (filter.type === 'category') {
      setSelectedFilters(prev => ({
        ...prev,
        categories: prev.categories.filter(c => c !== filter.label)
      }));
    } else if (filter.type === 'offer') {
      setSelectedFilters(prev => ({
        ...prev,
        offers: prev.offers.filter(o => o !== filter.label)
      }));
    } else if (filter.type === 'rx') {
      setSelectedFilters(prev => ({
        ...prev,
        needsPrescription: false
      }));
    } else if (filter.type === 'price') {
      setSelectedFilters(prev => ({
        ...prev,
        priceRange: {
          min: 0,
          max: 5000
        }
      }));
    }
  };
  const clearAllFilters = () => {
    setSelectedFilters({
      brands: [],
      priceRange: {
        min: 0,
        max: 5000
      },
      categories: [],
      needsPrescription: false,
      offers: []
    });
  };
  // Single banner row with 16:9 aspect ratio
  const banners = [{
    img: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 1'
  }, {
    img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 2'
  }];
  // Mock product data using home page structure
  const products = [{
    id: 1,
    name: 'Bambi Pants Box (Size) 6 X - LARGE * 68',
    brand: 'Bambi',
    price: 113.85,
    originalPrice: 145.0,
    discount: 22,
    rating: 4.8,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 2,
    name: 'Fine Baby Junior Jumbo Pack Size 6 Baby Diapers 22 Kg 38 Diapers',
    brand: 'Fine Baby',
    price: 54.51,
    originalPrice: 68.99,
    discount: 21,
    rating: 4.7,
    reviewCount: 203,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 3,
    name: 'Babyjoy Culotte Size 6 Junior Xxl 16+ Kg Giant Pack 50 Diaper Pants',
    brand: 'Babyjoy',
    price: 94.21,
    originalPrice: 120.0,
    discount: 21,
    rating: 4.9,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isNew: true
  }, {
    id: 4,
    name: 'Babyjoy Culotte Size 4 Large Jumbo Pack 8-10 Kg 44 Diaper Pants',
    brand: 'Babyjoy',
    price: 62.81,
    originalPrice: 79.99,
    discount: 21,
    rating: 4.6,
    reviewCount: 142,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 5,
    name: 'Pampers Premium Care Size 5 Junior 11-16 Kg 40 Diapers',
    brand: 'Pampers',
    price: 89.99,
    originalPrice: 110.0,
    discount: 18,
    rating: 4.8,
    reviewCount: 289,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 6,
    name: 'Huggies Ultra Comfort Size 4 Mega Pack 8-14 Kg 66 Diapers',
    brand: 'Huggies',
    price: 78.5,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 7,
    name: 'Pampers Active Baby Size 4 Maxi 9-14 Kg 82 Diapers',
    brand: 'Pampers',
    price: 95.99,
    originalPrice: 115.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 198,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 8,
    name: 'Babyjoy Compressed Size 5 Junior 12-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 72.5,
    originalPrice: 89.99,
    discount: 19,
    rating: 4.5,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 9,
    name: 'Fine Baby Smart Lock Size 5 Junior 11-18 Kg 60 Diapers',
    brand: 'Fine Baby',
    price: 68.99,
    originalPrice: 85.0,
    discount: 19,
    rating: 4.4,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 10,
    name: 'Huggies Dry Comfort Size 5 Junior 12-22 Kg 44 Diapers',
    brand: 'Huggies',
    price: 82.5,
    originalPrice: 99.99,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 11,
    name: 'Pampers Baby Dry Size 3 Midi 6-10 Kg 74 Diapers',
    brand: 'Pampers',
    price: 79.99,
    originalPrice: 95.0,
    discount: 16,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 12,
    name: 'Babyjoy Healthy Skin Size 4 Large 10-18 Kg 62 Diapers',
    brand: 'Babyjoy',
    price: 75.5,
    originalPrice: 92.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 13,
    name: 'Fine Baby Natural Touch Size 4 Maxi 7-14 Kg 50 Diapers',
    brand: 'Fine Baby',
    price: 58.99,
    originalPrice: 72.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 14,
    name: 'Huggies Snug & Dry Size 6 Extra Large 16+ Kg 52 Diapers',
    brand: 'Huggies',
    price: 86.5,
    originalPrice: 105.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 15,
    name: 'Pampers Pants Size 5 Junior 12-17 Kg 48 Pants',
    brand: 'Pampers',
    price: 92.99,
    originalPrice: 112.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 16,
    name: 'Bambi Nature Size 5 Junior 11-25 Kg 44 Diapers',
    brand: 'Bambi',
    price: 98.5,
    originalPrice: 118.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 17,
    name: 'Babyjoy Compressed Diamond Pad Size 6 XXL 16+ Kg 40 Diapers',
    brand: 'Babyjoy',
    price: 69.99,
    originalPrice: 85.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 18,
    name: 'Fine Baby Super Dry Size 3 Medium 4-9 Kg 68 Diapers',
    brand: 'Fine Baby',
    price: 62.5,
    originalPrice: 76.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 19,
    name: 'Huggies Little Snugglers Size 2 Newborn 3-6 Kg 84 Diapers',
    brand: 'Huggies',
    price: 74.99,
    originalPrice: 90.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 20,
    name: 'Pampers Swaddlers Size 1 Newborn 2-5 Kg 92 Diapers',
    brand: 'Pampers',
    price: 81.5,
    originalPrice: 98.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 21,
    name: 'Babyjoy New Born Size 1 Newborn 2-4 Kg 100 Diapers',
    brand: 'Babyjoy',
    price: 65.99,
    originalPrice: 80.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 22,
    name: 'Fine Baby Diaper Pants Size 5 Junior 12-22 Kg 36 Pants',
    brand: 'Fine Baby',
    price: 56.5,
    originalPrice: 68.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 23,
    name: 'Huggies Pull-Ups Size 4 Large 8-15 Kg 42 Training Pants',
    brand: 'Huggies',
    price: 77.99,
    originalPrice: 94.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 24,
    name: 'Pampers Cruisers Size 4 Maxi 9-14 Kg 70 Diapers',
    brand: 'Pampers',
    price: 88.5,
    originalPrice: 106.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 25,
    name: 'Bambi Premium Care Size 4 Maxi 8-16 Kg 56 Diapers',
    brand: 'Bambi',
    price: 102.99,
    originalPrice: 124.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 26,
    name: 'Babyjoy Twin Pack Size 5 Junior 12-18 Kg 2x58 Diapers',
    brand: 'Babyjoy',
    price: 135.5,
    originalPrice: 165.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 27,
    name: 'Fine Baby Mega Pack Size 6 XXL 16+ Kg 54 Diapers',
    brand: 'Fine Baby',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 28,
    name: 'Huggies Natural Care Size 3 Midi 5-9 Kg 78 Diapers',
    brand: 'Huggies',
    price: 83.5,
    originalPrice: 101.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 29,
    name: 'Pampers Pure Protection Size 2 Mini 4-8 Kg 88 Diapers',
    brand: 'Pampers',
    price: 93.99,
    originalPrice: 113.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 30,
    name: 'Babyjoy Culotte Mega Pack Size 3 Medium 6-11 Kg 56 Pants',
    brand: 'Babyjoy',
    price: 67.5,
    originalPrice: 82.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 31,
    name: 'Fine Baby Comfort Size 4 Large 7-14 Kg 62 Diapers',
    brand: 'Fine Baby',
    price: 64.99,
    originalPrice: 78.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 32,
    name: 'Huggies Overnight Size 5 Junior 12-22 Kg 48 Diapers',
    brand: 'Huggies',
    price: 89.5,
    originalPrice: 108.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 33,
    name: 'Pampers Splashers Size 4 Maxi 9-15 Kg 24 Swim Pants',
    brand: 'Pampers',
    price: 45.99,
    originalPrice: 55.0,
    discount: 16,
    rating: 4.5,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 34,
    name: 'Bambi Sensitive Size 3 Midi 4-9 Kg 72 Diapers',
    brand: 'Bambi',
    price: 96.5,
    originalPrice: 116.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 35,
    name: 'Babyjoy Super Soft Size 2 Mini 3-6 Kg 96 Diapers',
    brand: 'Babyjoy',
    price: 73.99,
    originalPrice: 90.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 36,
    name: 'Fine Baby Ultra Thin Size 5 Junior 11-18 Kg 52 Diapers',
    brand: 'Fine Baby',
    price: 66.5,
    originalPrice: 80.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 37,
    name: 'Huggies Gentle Care Size 1 Newborn 2-5 Kg 90 Diapers',
    brand: 'Huggies',
    price: 78.99,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 38,
    name: 'Pampers Harmonie Size 3 Midi 6-10 Kg 66 Diapers',
    brand: 'Pampers',
    price: 97.5,
    originalPrice: 117.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 39,
    name: 'Babyjoy Premium Dry Size 4 Large 10-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 40,
    name: 'Fine Baby Value Pack Size 4 Maxi 7-14 Kg 72 Diapers',
    brand: 'Fine Baby',
    price: 69.5,
    originalPrice: 84.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }];
  const nowAvailableProducts = isNowContext ? products.filter(product => {
    if (nowAvailability === 'limited') return product.id % 5 === 0;
    if (nowAvailability === 'available') return product.id % 5 !== 0;
    return true;
  }) : products;
  const totalPages = Math.ceil(nowAvailableProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = nowAvailableProducts.slice(startIndex, endIndex);
  return <div className="min-h-screen bg-gray-50 pt-[var(--header-height-mobile)] md:pt-0" data-id="element-7285" data-mp="2rrbmwj9prwh3">
      <MobileInternalHeader title={isNowContext ? pharmacyCategories.find(category => category.id === nowCategoryId)?.name ?? categoryName : categoryName} data-id="element-7286" data-mp="2mwnvabjeylya" />
      {isNowContext && <NowCategoryBrowseHeader activeCategoryId={nowCategoryId} activeSubcategory={nowSubcategory} onCategorySelect={categoryId => {
      setNowCategoryId(categoryId);
      setNowSubcategory('All');
    }} onSubcategorySelect={setNowSubcategory} data-id="element-7287" data-mp="2dzn9mcnqb1yj" />}
      {!isNowContext && <>
      {/* Banner Row - 16:9 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 py-1 md:py-4 pt-2 md:pt-4" data-id="element-7288" data-mp="jjybypzvex76">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7289" data-mp="of3wplhje5to">
          {banners.map((banner, index) => <a key={index} href={banner.link} className="block rounded-xl overflow-hidden bg-gray-300 hover:shadow-md transition-shadow relative" data-id="element-7290" data-mp="tdx7v79cpkqo" data-mpi={index}>
              <div className="relative w-full" style={{
              paddingTop: '56.25%'
            }} data-id="element-7291" data-mp="pjb0uu00ugp">
                <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-8 text-gray-800" data-id="element-7292" data-mp="9r92u1rslvf0">
                  <span className="text-xs font-bold bg-white text-gray-600 px-3 py-1 rounded-full w-max mb-3" data-id="element-7293" data-mp="ekbyqgk1lgyd">
                    DIMENSIONS
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-7294" data-mp="3i72iyfi7p4vj">
                    BANNER SIZE
                  </h2>
                  <p className="text-sm md:text-base font-medium mb-3" data-id="element-7295" data-mp="pc2k8la7zbwy">
                    50% width (desktop) | 100% width (mobile)
                  </p>
                  <button className="bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded-md w-max text-sm" data-id="element-7296" data-mp="z2x97xeuopm3">
                    Ratio 16:9
                  </button>
                </div>
              </div>
            </a>)}
        </div>
      </div>
      {/* 6 Banner Row - 3:1 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-1 md:pb-4" data-id="element-7297" data-mp="1g83pdkxvuvh">
        {/* Desktop View - 6 banners in a row */}
        <div className="hidden md:grid md:grid-cols-6 gap-3" data-id="element-7298" data-mp="fgr541uwg2oo">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="rounded-lg overflow-hidden relative bg-gray-300 transform transition-transform hover:scale-105" data-id="element-7299" data-mp="bcb52h7yvphd">
              <div className="w-full h-32" data-id="element-7300" data-mp="1mshr0cz9pgts"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-3 text-gray-800" data-id="element-7301" data-mp="1ubr1k9jiflkf">
                <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-7302" data-mp="1zbwq3guhgrya">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-1" data-id="element-7303" data-mp="1kgh13q8rfm8r">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-7304" data-mp="1pdpihom33m8y">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
        {/* Mobile View - Horizontal Scroll */}
        <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-3 pb-2 -mx-4 px-4" data-id="element-7305" data-mp="2i0tmbq9fxuj5">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="flex-none w-[70vw] snap-center rounded-lg overflow-hidden relative bg-gray-300" data-id="element-7306" data-mp="28bs6q16t5f5p">
              <div className="w-full h-32" data-id="element-7307" data-mp="30scl1kwfmmfd"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-4 text-gray-800" data-id="element-7308" data-mp="30t4hsz1rmq78">
                <span className="text-[10px] font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-1.5" data-id="element-7309" data-mp="1d52p7yv6hynh">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-0.5" data-id="element-7310" data-mp="135llhmzhluu5">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-7311" data-mp="137of0gzum63m">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
      </div>
        </>}
      {/* Filters + Sort + Product Grid */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-12 md:pb-12 pb-32" data-id="element-7312" data-mp="1qvfshnx4kp6h">
        {/* Mobile Results Count Bar */}
        {!isNowContext && <div className="md:hidden py-3 px-4 flex items-center justify-between -mx-4 mb-2 bg-white border-b border-gray-100" data-id="element-7313" data-mp="1m3em2imn1sod">
          <div className="text-sm font-bold text-gray-900" data-id="element-7314" data-mp="1h5cu1878ij0r">
            {products.length} products
          </div>
          <div className="text-xs text-gray-500 font-medium" data-id="element-7315" data-mp="2emv1u0nbk6r8">
            Results: {startIndex + 1}-{Math.min(endIndex, products.length)}
          </div>
        </div>}

        {/* Active Filter Chips Row (Mobile Only) */}
        {activeFilters.length > 0 && <div className="md:hidden flex overflow-x-auto hide-scrollbar gap-2 mb-4 -mx-4 px-4 pb-1" data-id="element-7316" data-mp="2m5b2woh7x7w1">
            {activeFilters.map(filter => <button key={filter.id} onClick={() => removeFilter(filter)} className="flex-none flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-100 text-[#0066CC] rounded-full text-sm font-medium whitespace-nowrap" data-id="element-7317" data-mp="2w2pcc44q4weu">
                {filter.label}
                <XIcon className="w-3.5 h-3.5" data-id="element-7318" data-mp="39tuaf1sg1gn2" />
              </button>)}
            <button onClick={clearAllFilters} className="flex-none px-3 py-1.5 text-gray-500 text-sm font-medium underline whitespace-nowrap" data-id="element-7319" data-mp="3etqaipy0snxc">
              Clear all
            </button>
          </div>}

        {/* Desktop Layout: Sidebar + Grid */}
        <div className="flex gap-6" data-id="element-7320" data-mp="2z2pm86ol3t78">
          {/* Desktop Filter Sidebar */}
          <div className="hidden md:block w-64 lg:w-72 flex-shrink-0" data-id="element-7321" data-mp="2u46128htelfx">
            <div className="sticky top-24" data-id="element-7322" data-mp="2u21kapythry8">
              <FilterSidebar selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} hideCategories={isNowContext} data-id="element-7323" data-mp="38tkb7it7t6wd" />
            </div>
          </div>
          {/* Product Grid */}
          <div className="flex-1" data-id="element-7324" data-mp="3hvtj00x3mm62">
            {/* Results count and sort */}
            {!isNowContext && <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-lg" data-id="element-7325" data-mp="3cx00tv6va6zk">
              <p className="text-sm text-gray-600" data-id="element-7326" data-mp="3rpk4qy1ucii2">
                Results:{' '}
                <span className="font-medium text-gray-900" data-id="element-7327" data-mp="3mtl70jumho4z">
                  {nowAvailableProducts.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, nowAvailableProducts.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium text-gray-900" data-id="element-7328" data-mp="7x02wtvnlo2s">
                  {nowAvailableProducts.length}
                </span>
              </p>
              {/* Desktop sort dropdown */}
              <div className="hidden md:flex items-center gap-2" data-id="element-7329" data-mp="3ucchf8p30uif">
                <span className="text-sm text-gray-600" data-id="element-7330" data-mp="3uc2obc8qq78i">Sort By</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" data-id="element-7331" data-mp="34zyao4n26zv">
                  <option value="best-match" data-id="element-7332" data-mp="3be02t7feisfg">Recommended</option>
                  <option value="top-rated" data-id="element-7333" data-mp="3bfkveri5o1ao">Top Rated</option>
                  <option value="price-low-high" data-id="element-7334" data-mp="3gdcpns69vzi1">Price: Low to High</option>
                  <option value="price-high-low" data-id="element-7335" data-mp="19llla9grrpzk">Price: High to Low</option>
                  <option value="newest" data-id="element-7336" data-mp="1el6lsobl86ee">Newest</option>
                </select>
              </div>
              </div>}
            {/* Product grid switches to the compact NOW 120 card only when entered from NOW. */}
            <div className={`grid gap-2 md:gap-4 mb-8 ${isNowContext ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' : 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`} data-id="element-7337" data-mp="3thw6vrrsn1jw">
              {paginatedProducts.map(product => isNowContext ? <NowCompactProductCard key={product.id} product={product} data-id="element-7338" data-mp="3etrl2exg29ww" /> : <EnhancedProductCard key={product.id} product={product} data-id="element-7339" data-mp="39u57g4dpernk" />)}
            </div>
            {isNowContext && paginatedProducts.length === 0 && <div className="mb-8 rounded-xl border border-brand-border bg-white p-6 text-center" data-id="element-7340" data-mp="35str6pgpzis1">
                <p className="text-sm font-bold text-brand-ink" data-id="element-7341" data-mp="30vbin06m75kk">No products match this availability filter.</p>
                <button type="button" onClick={() => setNowAvailability('all')} className="mt-3 text-sm font-bold text-brand" data-id="element-7342" data-mp="13yuhu9n5rbhz">Show all nearby products</button>
              </div>}
            {/* Pagination */}
            <Pagination currentPage={currentPage} totalPages={totalPages} data-id="element-7343" data-mp="1bkg80cporeii" />
          </div>
        </div>
      </div>

      {/* Floating Filter & Sort FAB (Mobile Only) */}
      <AnimatePresence data-id="element-7344" data-mp="1gdj0ehiq8hx4">
        {showFab && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="md:hidden fixed bottom-[72px] left-0 right-0 mx-auto w-max z-40 flex items-center bg-brand text-white rounded-xl shadow-2xl overflow-hidden" data-id="element-7345" data-mp="1lasp1uyakpjw">
            <button onClick={() => setShowMobileSort(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 border-r border-white/20 active:bg-white/10 transition-colors" data-id="element-7346" data-mp="11kflyj0fmkam">
              <ArrowUpDownIcon className="w-3.5 h-3.5" data-id="element-7347" data-mp="342yniv0fb9gm" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-7348" data-mp="345l5zpour9dm">
                Sort
              </span>
            </button>
            <button onClick={() => setShowMobileFilters(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 active:bg-white/10 transition-colors relative" data-id="element-7349" data-mp="2ue8cyurbid60">
              <FilterIcon className="w-3.5 h-3.5" data-id="element-7350" data-mp="2lbz3873vinto" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-7351" data-mp="2lfd5lfcjex14">
                Filter
              </span>
              {activeFilters.length > 0 && <span className="absolute top-1.5 right-2 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-[#0066CC]" data-id="element-7352" data-mp="2bgdwxwrrv1e4">
                  {activeFilters.length}
                </span>}
            </button>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile Filter Modal */}
      <MobileFilterModal isOpen={showMobileFilters} onClose={() => setShowMobileFilters(false)} selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} hideCategories={isNowContext} data-id="element-7353" data-mp="1z3trmxfzs6bx" />
      {/* Mobile Sort Modal */}
      <MobileSortModal isOpen={showMobileSort} onClose={() => setShowMobileSort(false)} sortBy={sortBy} onSortChange={setSortBy} data-id="element-7354" data-mp="243y5sgtfgoyz" />
    </div>;
}